import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, FileText, Shield, RefreshCw } from 'lucide-react';
import TermsContent from '../components/Legal/TermsContent';
import PrivacyContent from '../components/Legal/PrivacyContent';
import RefundContent from '../components/Legal/RefundContent';
import { JsonLd } from '../components/Common/JsonLd';

type Section = 'terms' | 'privacy' | 'refund';

export default function Legal() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<Section>('terms');

  const sections = [
    { id: 'terms' as Section, icon: FileText, label: 'Terms of Service', color: 'blue' },
    { id: 'privacy' as Section, icon: Shield, label: 'Privacy Policy', color: 'green' },
    { id: 'refund' as Section, icon: RefreshCw, label: 'Refund Policy', color: 'orange' },
  ];

  return (
    <div className="theme-fixed min-h-screen bg-gray-950 text-white">
      <Helmet>
        <title>Legal Documents · HitzLink</title>
        <meta
          name="description"
          content="HitzLink legal hub — terms of service, privacy policy, and refund policy in one place."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://hitzlink.com/legal" />
      </Helmet>

      <JsonLd
        id="legal-webpage"
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          '@id': 'https://hitzlink.com/legal',
          url: 'https://hitzlink.com/legal',
          name: 'Legal Documents',
          description: 'HitzLink terms of service, privacy policy, and refund policy.',
          inLanguage: 'th',
          isPartOf: { '@id': 'https://hitzlink.com#website' },
          publisher: { '@id': 'https://hitzlink.com#organization' },
          hasPart: [
            { '@type': 'WebPage', name: 'Terms of Service', url: 'https://hitzlink.com/terms' },
            { '@type': 'WebPage', name: 'Privacy Policy', url: 'https://hitzlink.com/privacy' },
          ],
        }}
      />

      {/* Header */}
      <div className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg border border-gray-800 p-4 sticky top-4">
              <h2 className="text-base font-semibold text-white mb-4 px-2">Legal Information</h2>
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-medium ${
                        isActive
                          ? 'bg-gray-800 text-white border border-gray-700'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                      }`}
                    >
                      <Icon size={18} />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="mt-6 pt-4 border-t border-gray-800 px-2">
                <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                <p className="text-xs text-gray-300">
                  {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </aside>

          {/* Content Area */}
          <main className="flex-1 min-w-0">
            <div className="bg-gray-900/30 backdrop-blur-sm rounded-lg border border-gray-800 p-6 sm:p-8">
              {activeSection === 'terms' && <TermsContent />}
              {activeSection === 'privacy' && <PrivacyContent />}
              {activeSection === 'refund' && <RefundContent />}
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div className="text-center sm:text-left">
              <div>© {new Date().getFullYear()} Hitz Tech Solutions Co., Ltd. All rights reserved.</div>
              <div className="text-xs text-gray-500 mt-0.5">บริษัท ฮิตซ์เทค โซลูชั่นส์ จำกัด</div>
            </div>
            <div className="flex gap-6">
              <button onClick={() => navigate('/')} className="hover:text-white transition-colors">กลับหน้าแรก</button>
              <a href="mailto:support@hitzlink.com" className="hover:text-white transition-colors">สนับสนุน</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

